/*
 * This file contains a floorplan details component that displays the details of a floorplan.
 */

import React, { useEffect, useState, Suspense,useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import styles from "./floorplanDetails.module.scss";
import ellipse from "../../../assets/images/ellipse.svg";
import clsx from "clsx";
import {
  floorMapper,
  formatCurrency,
  getAvailabilityDate,
  getSpecialsList,
  getBreadcrumbUrl,
  getUnitListingBreadcrumbUrl,
} from "../../../utils/util";
import fetchData from "../../../utils/fetchData";
import { traceEvent, traceFirstMeaningfulAction } from "../../../utils/tracing";
import MoveInDatePicker from "../moveinDateModal/moveinDateModal";
import specialIcon from "../../../assets/images/specials-icon.svg";
import FloorplanDetailsSkelton from "./floorplanDetailsSkelton";
import defaultImage from "../../../assets/images/no-image.png";

function FloorplanDetails() {
  const [searchParams] = useSearchParams();
  const params = useMemo(() => searchParams.toString(), [searchParams]);
  const [moveInStartDate, setMoveInStartDate] = useState(
    searchParams.get("moveInStartDate") || null
  );
  const { floorplanId, apartmentId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [notFoundMsg, setNotFoundMsg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [specials, setSpecials] = useState(null);
  const [amenities, setAmenities] = useState(null);

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  // This function handles the image error event.
  // It prevents infinite loops in case the fallback also fails and sets the image source to the default image.
  const handleImageError = (e) => {
    e.target.onerror = null; // Prevent infinite loops in case the fallback also fails
    e.target.src = defaultImage;
  };

  // This effect fetches the floor plan data and updates the state accordingly.
  // It fetches the data from the API and updates the data state if the response contains valid floor plan data.
  // If not, it sets the error state and displays an error message.
  useEffect(() => {
    const fetchDataAsync = async () => {
      try {
        const floorplanResponse = await fetchData(
          `/wp-json/entrata/v1/getFloorPlan/${floorplanId}/${apartmentId}${
            moveInStartDate ? `?moveInStartDate=${moveInStartDate}` : ""
          }`
        );

        if (
          floorplanResponse &&
          floorplanResponse?.fp &&
          floorplanResponse?.fp?.length > 0
        ) {
          setData(floorplanResponse?.fp[0]);
          setNotFoundMsg(floorplanResponse?.config);
          setSpecials(floorplanResponse?.specials);

          // Fetch amenities after we have the floorplan data
          const amenitiesResponse = await fetchData(
            `/wp-json/entrata/v1/amenities?floorplan_id=${
              floorplanResponse.fp[0].floorplan_name.includes("PH") ? "ph" : "*"
            }&floor_level=${
              floorplanResponse.fp[0].floorplan_name.includes("PH")
                ? floorplanResponse.fp[0].units[0]?.floor_number
                : "*"
            }`
          );

          if (amenitiesResponse && amenitiesResponse.success) {
            setAmenities(amenitiesResponse.data);
          }
        } else {
          setNotFoundMsg({
            contact_page_url: "/contact",
            no_result_message:
              "Oh no! It looks like we currently do not have anything available within the search parameters given. Please contact [url] our leasing team directly for details on future availability that may match your search. We look forward to assisting you",
          });
          throw new Error("No floor plan data available.");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchDataAsync();
  }, [floorplanId, apartmentId, moveInStartDate]);

  useEffect(() => {
    if (!loading && data && !sessionStorage.getItem("detailPageTracked")) {
      sessionStorage.setItem("detailPageTracked", "true");
      traceEvent("FLOOR_PLAN_DETAIL_PAGE_RESULT");
    }
  }, [data, loading]);

  // This conditional renders a skeleton loader while the data is being fetched.
  if (loading) {
    return <FloorplanDetailsSkelton />;
  }

  // This conditional renders an error message if there is an error.
  if (error) {
    return (
      <div className={styles.errorContainer}>
        <div>
          <h3>No results found</h3>
          <p>
            {notFoundMsg?.no_result_message?.split("contact [url]")[0]}
            <a href={notFoundMsg?.contact_page_url}>contact</a>
            {notFoundMsg?.no_result_message?.split("contact [url]")[1]}
          </p>
        </div>
      </div>
    );
  }

  // This function gets the list of specials for the floorplan.
  const specialsList = getSpecialsList(specials);

  // This conditional renders an error message if there is no data or if the units array is empty.
  if (!data || data?.units?.length === 0) {
    return (
      <div className={styles.errorContainer}>
        <div>
          <h3>No results found</h3>
          <p>
            {notFoundMsg?.no_result_message?.split("contact [url]")[0]}
            <a href={notFoundMsg?.contact_page_url}>contact</a>
            {notFoundMsg?.no_result_message?.split("contact [url]")[1]}
          </p>
        </div>
      </div>
    );
  }

  return (
    <Suspense fallback={<div>Loading...</div>}>
      <div
        id="floor-plan-details-template"
        className={styles.floorplanDetailsWrapper}
      >
        <div className={styles.mapSection}>
          <div className={styles.navigationPath}>
            <a
              href={getBreadcrumbUrl(params)}
              onClick={() => traceEvent("FLOORPLAN_DETAILS_RETURN_TO_RESULTS")}
            >
              Floor Plan
            </a>
            &nbsp;/&nbsp;
            <a
              href={getUnitListingBreadcrumbUrl(
                data?.floorplan_name,
                data?.floorplan_id,
                params
              )}
              onClick={() => traceEvent("FLOORPLAN_DETAILS_RETURN_TO_RESULTS")}
            >
              {data?.floorplan_name}
            </a>
            &nbsp;/&nbsp;
            <span>{data?.units[0]?.marketing_name}</span>
          </div>
          <div className={styles.specialIcons}>
          {specialsList?.includes(floorplanId) && (
              <>
                <img src={specialIcon} alt="Special" />
                {specials?.floorplan[floorplanId]?.description}*
              </>
            )}
          </div>
          <div className={styles.mapContainer}>
            <img
              className={clsx(
                styles.mapImage,
                !data?.floorplan_image && styles.noImage
              )}
              src={data?.floorplan_image || defaultImage}
              alt="Floorplan Map"
              onError={handleImageError}
            />
          </div>
          <div className={styles.disclaimer}>
            <p>
              Floor plans are for illustrative purposes only. Actual layout and
              square footage may vary.
            </p>

            {specialsList?.includes(floorplanId) && (
              <p className={styles.restrictions}>* Restrictions Apply</p>
            )}
          </div>
        </div>
        <div className={styles.detailsSection}>
          <h2 className={styles.title}>{data?.units[0]?.marketing_name}</h2>
          <p className={styles.nameFloor}>
            <span>{data?.floorplan_name}</span>
            <img src={ellipse} alt="Ellipse" />
            <span>
              {data?.units[0]?.floor_number}
              {floorMapper(data?.units[0]?.floor_number)}
            </span>{" "}
            Floor
          </p>
          <div className={styles.featuresWrapper}>
            <ul className={styles.Features}>
              <li>
                {/* This line checks if the bed count is 0 and returns "Studio" if true, otherwise it returns the number of bedrooms */}
                {data?.units[0]?.bed_count === "0"
                  ? "Studio"
                  : `${data?.units[0]?.bed_count} Bedroom`}
              </li>
              <li>{parseFloat(data?.units[0]?.bath_count)} Bath</li>
            </ul>
            <div>
              <span>{data?.units[0]?.sqft}</span> SQ.FT.
            </div>
          </div>
          <hr />
          <div className={styles.priceAvailability}>
            {/* This section dynamically displays the pricing information based on the availability of the best_price and its value. If best_price is not null and greater than 0, it displays the minimum rent formatted as currency. Otherwise, it prompts the user to call for pricing. */}
            {data?.units[0]?.best_price !== null &&
            parseInt(data?.units[0]?.best_price) > 0 ? (
              <p>
                From <span>{formatCurrency(data?.units[0]?.min_rent)}</span>
              </p>
            ) : (
              <p>Call for pricing</p>
            )}
            <p className={styles.availability}>
              {getAvailabilityDate(
                data?.units[0]?.availability_date,
                data?.units[0]?.unit_status
              ) !== "" && (
                <>
                  Available&nbsp;
                  {getAvailabilityDate(
                    data?.units[0]?.availability_date,
                    data?.units[0]?.unit_status
                  )}
                </>
              )}
            </p>
          </div>
          <div className={styles.buttonContainer}>
            {/* This section dynamically displays a call-to-action button based on the availability of the best_price. If best_price is not null and greater than 0, it displays an "Apply Now" button linking to the availability URL. Otherwise, it displays a "Get Notified" button linking to the contact page URL. */}
            {data?.units[0]?.best_price !== null &&
            parseInt(data?.units[0]?.best_price) > 0 ? (
              <a
                href={`${
                  process.env.REACT_APP_FP_LEASE_TERM_URL
                }/property[id]/${
                  data?.units[0]?.apartment_id
                }/property_floorplan[id]/${data?.floorplan_id}/unit_space[id]/${
                  data?.units[0]?.unit_space_id
                }/show_in_popup/false/from_check_availability/1/?lease_start_date=${
                  new Date() > new Date(data?.units[0]?.availability_date) 
                    ? new Date().toISOString().split('T')[0]
                    : data?.units[0]?.availability_date
                }`}
                target="_blank"
                rel="noreferrer"
                className={clsx(styles.Btn, styles.primary, "apply-now-btn")}
                onClick={() => { traceFirstMeaningfulAction(); traceEvent("FLOORPLAN_APPLY_NOW_DETAIL"); }}
              >
                Apply Now
              </a>
            ) : (
              <a
                href={notFoundMsg?.contact_page_url}
                className={clsx(styles.Btn, styles.primary)}
              >
                Get Notified
              </a>
            )}
              {data?.units[0]?.best_price !== null &&
              parseInt(data?.units[0]?.best_price) > 0 && (
                <button
                className={clsx(styles.Btn, styles.secondary)}
                onClick={() => {
                  traceEvent("LEASE_TERMS_BTN");
                  openModal();
                }}
              >
                Lease Terms
              </button>
            )}
          </div>
          <div className={styles.details} style={{ display: "none" }}>
            <h3>Details</h3>
            <p>
              Bringing a furry friend along? Find out upfront how much your
              total monthly cost will be with added extras.
            </p>
          </div>
          {amenities && amenities.length > 0 && amenities.some(amenity =>   
            amenity?.amenities_items && 
            amenity.amenities_items.length > 0
          ) && (
            <div className={styles.amenities}>
              <h3>Featured Amenities</h3>
              <ul>
                {amenities?.map((amenity, index) =>
                  amenity?.amenities_items
                    .split(",\n")
                    .filter((item) => item.trim() !== "")
                    .map((item, itemIndex) => (
                      <li key={`${index}-${itemIndex}`}>
                        <img src={ellipse} alt="Ellipse" />
                        <span>{item.trim()}</span>
                      </li>
                    ))
                )}
              </ul>

              <span className={styles.amenitiesDisclaimer}>
                *Some features are not available in all suites.
              </span>
            </div>
          )}
        </div>
      </div>
      <MoveInDatePicker
        isOpen={isModalOpen}
        onClose={closeModal}
        floorplanId={floorplanId}
        unitId={apartmentId}
        moveInStartDate={moveInStartDate}
        allowedDate={data?.units[0]?.availability_date}
      />
    </Suspense>
  );
}

export default FloorplanDetails;
